import { useEffect, useRef, type RefObject } from "react";
import { DefaultBrowserBehavior } from "amazon-chime-sdk-js";
import { usePlatformStore } from "@platform/store";
import { VIDEO_HEALTH_CHECK_INTERVAL_MS } from "@core/features/video/video.constants";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";

/**
 * Detects when a <video> element has no decodable frames while the tile
 * state says 'video'. After 2 consecutive failed checks, force-transitions
 * the attendee to 'frozen' so the canvas fallback activates.
 *
 * Also monitors tiles it froze: when the video element recovers (valid
 * readyState and dimensions), transitions back to 'video'. Only recovers
 * tiles frozen by this hook — bandwidth-paused, source-loss, and
 * reconnect-guard frozen tiles are untouched.
 *
 * Detection has two layers:
 * 1. Dead element: readyState < 2 or videoWidth/videoHeight === 0
 *    (Chrome clears these when the track becomes undecodable)
 * 2. Stale buffer: readyState and dimensions look valid, but
 *    getVideoPlaybackQuality().totalVideoFrames stops advancing
 *    (Chrome holds the last decoded frame in the buffer)
 *
 * Layer 2 addresses the documented blind spot where Chrome holds valid
 * readyState/videoWidth on stale decoded frames.
 */
export function useVideoHealthCheck(
  videoRef: RefObject<HTMLVideoElement | null>,
  attendeeId: string,
  tileState: string,
) {
  const failCountRef = useRef(0);
  const frozenByHealthCheckRef = useRef(false);
  const freezeStartRef = useRef<number | null>(null);
  const lastFrameCountRef = useRef<number | null>(null);
  const staleCountRef = useRef(0);
  const setAttendeeTileState = usePlatformStore((s) => s.setAttendeeTileState);

  useEffect(() => {
    // DefaultBrowserBehavior is constructed inline (mirrors useDeviceHotPlug
    // and AudioOutputControl). Per-effect-run cost is one allocation per tile
    // mount, not per tick — negligible.
    const isChromiumWebRTC = new DefaultBrowserBehavior().hasChromiumWebRTC();

    // Reset tracking when tile leaves frozen for other reasons
    if (tileState !== 'frozen') {
      frozenByHealthCheckRef.current = false;
    }

    // Monitor both 'video' (detect dead) and 'frozen' (detect recovery)
    if (tileState !== 'video' && tileState !== 'frozen') {
      failCountRef.current = 0;
      lastFrameCountRef.current = null;
      staleCountRef.current = 0;
      return;
    }

    const id = setInterval(() => {
      const el = videoRef.current;
      if (!el) return;

      const isDead =
        el.readyState < 2 || el.videoWidth === 0 || el.videoHeight === 0;

      // Stale-buffer detection: Chrome can report valid readyState/videoWidth
      // on frames that stopped advancing. Check totalVideoFrames counter.
      // Gated on hasChromiumWebRTC() — Safari and Firefox both implement
      // getVideoPlaybackQuality(), but their decoders stall longer than the
      // 6s threshold on simulcast layer transitions, producing false-positive
      // freezes (verified in production meeting 5937: ~1,800 false positives
      // concentrated entirely on Safari and Firefox observers in a 30-min
      // session, with clean network telemetry across the meeting). The
      // dead-element check above runs on all browsers; only this stale-buffer
      // branch is Chromium-only. Mirrors the §8 simulcast-policy gate in
      // joinChimeSession.
      let isStale = false;
      if (!isDead && tileState === 'video' && isChromiumWebRTC) {
        const quality = (el as HTMLVideoElement & { getVideoPlaybackQuality?: () => { totalVideoFrames: number } }).getVideoPlaybackQuality?.();
        if (quality) {
          const currentFrames = quality.totalVideoFrames;
          if (lastFrameCountRef.current !== null && currentFrames === lastFrameCountRef.current) {
            staleCountRef.current++;
            // Require 2 consecutive stale checks (~6s) to avoid false positives
            // from brief pauses during simulcast layer switches
            if (staleCountRef.current >= 2) {
              isStale = true;
            }
          } else {
            staleCountRef.current = 0;
          }
          lastFrameCountRef.current = currentFrames;
        }
      }

      if (isDead || isStale) {
        if (tileState === 'video') {
          // For isDead, require 2 consecutive failures (existing behavior).
          // For isStale, the staleCountRef already accumulated 2 checks.
          if (isStale || ++failCountRef.current >= 2) {
            setAttendeeTileState(attendeeId, 'frozen');
            frozenByHealthCheckRef.current = true;
            freezeStartRef.current = Date.now();
            failCountRef.current = 0;
            staleCountRef.current = 0;
            lastFrameCountRef.current = null;

            // Log freeze event (fire-and-forget)
            const store = usePlatformStore.getState();
            logMeetingEvent({
              event_type: 'health_check_freeze',
              meeting_id: store.meetingId,
              room_id: store.roomId,
              user: store.user ? { id: store.user.id } : null,
              attendee_id: attendeeId,
              ready_state: el.readyState,
              video_width: el.videoWidth,
              video_height: el.videoHeight,
              stale_buffer: isStale,
            });
          }
        }
      } else {
        failCountRef.current = 0;
        // staleCountRef is owned by the inner stale-detection block above —
        // it's reset there when frames actually advance (the inner else
        // branch). Resetting it here would clobber accumulated stale-tick
        // count between consecutive stuck-frame ticks, since each healthy-
        // looking-but-stuck tick takes this branch (isDead && isStale are
        // both false until the counter reaches the threshold).
        // Recovery: only recover tiles frozen by THIS hook
        if (tileState === 'frozen' && frozenByHealthCheckRef.current) {
          setAttendeeTileState(attendeeId, 'video');

          // Log recovery event with freeze duration
          const freezeDuration = freezeStartRef.current
            ? Math.round((Date.now() - freezeStartRef.current) / 1000)
            : null;
          const store = usePlatformStore.getState();
          logMeetingEvent({
            event_type: 'health_check_recovery',
            meeting_id: store.meetingId,
            room_id: store.roomId,
            user: store.user ? { id: store.user.id } : null,
            attendee_id: attendeeId,
            freeze_duration_seconds: freezeDuration,
          });

          frozenByHealthCheckRef.current = false;
          freezeStartRef.current = null;
          lastFrameCountRef.current = null;
        }
      }
    }, VIDEO_HEALTH_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(id);
      failCountRef.current = 0;
      staleCountRef.current = 0;
      lastFrameCountRef.current = null;
    };
  }, [tileState, attendeeId, videoRef, setAttendeeTileState]);
}
